import { interpolate, spring } from 'remotion';

// Constants
import {
  HOLD_DURATION_AFTER_COMPLETE,
  MIN_FEEDBACK_DURATION,
  TIMING,
  LAYOUT_CONFIG,
  MAX_DISPLAY_FEEDBACKS,
  PROJECT_CODE_DELAY_FRAMES,
} from './constants';
import { ANIMATION_SPRING_CONFIGS, CLAMP_BOTH } from '@/constants';

// Types
import { FeedbackData, FeedbackTiming, LayoutVariant } from './types';
import { AnimationPreset } from '@/types';

/**
 * Get the max 3 newest feedbacks
 * @param feedbacks - Array of feedback data
 * @returns Max 3 feedbacks from the start of array
 */
export const getRecentFeedbacks = (
  feedbacks: FeedbackData[],
): FeedbackData[] => {
  if (!feedbacks || feedbacks.length === 0) return [];

  if (feedbacks.length <= MAX_DISPLAY_FEEDBACKS) return feedbacks;

  return feedbacks.slice(0, MAX_DISPLAY_FEEDBACKS);
};

// Opacity when exiting: goes from 1 to 0 in the first half of the exit
export const getExitOpacity = (exitProgress: number): number =>
  interpolate(exitProgress, [0, 0.5], [1, 0], CLAMP_BOTH);

// Feedback duration: delay + typing + project name delay + hold
export const calculateFeedbackDuration = (feedback: FeedbackData): number => {
  const typingDuration = Math.ceil(
    (feedback?.feedbackHighlight?.length || 0) / TIMING.QUOTE.charsPerFrame,
  );

  const naturalDuration =
    TIMING.QUOTE.startFrame +
    typingDuration +
    PROJECT_CODE_DELAY_FRAMES +
    HOLD_DURATION_AFTER_COMPLETE;

  return Math.max(naturalDuration, MIN_FEEDBACK_DURATION);
};

// Profile image: slides in/out (direction depends on layout)
export const getProfileAnimation = ({
  localFrame,
  fps,
  variant,
  isExiting,
  exitProgress,
}: {
  localFrame: number;
  fps: number;
  variant: LayoutVariant;
  isExiting: boolean;
  exitProgress: number;
}) => {
  const config = LAYOUT_CONFIG[variant];

  const profileProgress = spring({
    frame: localFrame - TIMING.PROFILE_IMAGE.startFrame,
    fps,
    config: ANIMATION_SPRING_CONFIGS[AnimationPreset.CONTAINER],
  });

  const opacity = isExiting
    ? getExitOpacity(exitProgress)
    : interpolate(profileProgress, [0, 0.5], [0, 1], CLAMP_BOTH);

  // On enter: move from edge to center
  const entryTranslateX = interpolate(
    profileProgress,
    [0, 1],
    [
      config.profileDirection === 'left'
        ? -TIMING.PROFILE_IMAGE.distance
        : TIMING.PROFILE_IMAGE.distance,
      0,
    ],
    CLAMP_BOTH,
  );

  // On exit: slide back toward the edge
  const exitTranslateX = interpolate(
    exitProgress,
    [0, 1],
    [
      0,
      config.profileDirection === 'left'
        ? TIMING.PROFILE_IMAGE.distance
        : -TIMING.PROFILE_IMAGE.distance,
    ],
    CLAMP_BOTH,
  );

  const translateX = isExiting ? exitTranslateX : entryTranslateX;

  return { opacity, translateX };
};

// Name and title: fade/scale in with optional delay so title comes after name
export const getNameTitleAnimation = ({
  localFrame,
  fps,
  isExiting,
  exitProgress,
  isTitle = false,
}: {
  localFrame: number;
  fps: number;
  isExiting: boolean;
  exitProgress: number;
  isTitle?: boolean;
}) => {
  const delay = isTitle ? TIMING.NAME.stagger : 0;

  const progress = spring({
    frame: localFrame - TIMING.NAME.startFrame - delay,
    fps,
    config: ANIMATION_SPRING_CONFIGS[AnimationPreset.DEFAULT],
  });

  const opacity = isExiting
    ? getExitOpacity(exitProgress)
    : interpolate(progress, [0, 0.5], [0, 1], CLAMP_BOTH);

  const scale = isExiting
    ? interpolate(exitProgress, [0, 1], [1, 0.9], CLAMP_BOTH)
    : interpolate(progress, [0, 1], [0.9, 1], CLAMP_BOTH);

  return { opacity, scale };
};

// Quotation marks: bounce in with rotation
export const getQuotationAnimation = ({
  localFrame,
  fps,
  variant,
  isExiting,
  exitProgress,
}: {
  localFrame: number;
  fps: number;
  variant: LayoutVariant;
  isExiting: boolean;
  exitProgress: number;
}) => {
  const config = LAYOUT_CONFIG[variant];

  const quotationProgress = spring({
    frame: localFrame - TIMING.QUOTATION_MARKS.startFrame,
    fps,
    config: ANIMATION_SPRING_CONFIGS[AnimationPreset.BOUNCY],
  });

  const scale = isExiting
    ? interpolate(exitProgress, [0, 1], [1, 0], CLAMP_BOTH)
    : interpolate(quotationProgress, [0, 1], [0, 1], CLAMP_BOTH);

  // On enter: rotate from tilted to flat
  const entryRotate = interpolate(
    quotationProgress,
    [0, 1],
    [config.quotationPosition === 'left' ? 15 : -15, 0],
    CLAMP_BOTH,
  );

  // On exit: rotate back out
  const exitRotate = interpolate(
    exitProgress,
    [0, 1],
    [0, config.quotationPosition === 'left' ? -15 : 15],
    CLAMP_BOTH,
  );

  const rotate = isExiting ? exitRotate : entryRotate;

  return { scale, rotate };
};

// Project code: appears after quote finishes typing
export const getProjectCodeAnimation = ({
  localFrame,
  fps,
  feedbackLength,
  isExiting,
  exitProgress,
}: {
  localFrame: number;
  fps: number;
  feedbackLength: number;
  isExiting: boolean;
  exitProgress: number;
}) => {
  const typingDuration = Math.ceil(feedbackLength / TIMING.QUOTE.charsPerFrame);
  const projectCodeStartLocal =
    TIMING.QUOTE.startFrame + typingDuration + PROJECT_CODE_DELAY_FRAMES;

  const projectProgress = spring({
    frame: localFrame - projectCodeStartLocal,
    fps,
    config: ANIMATION_SPRING_CONFIGS[AnimationPreset.GENTLE],
  });

  const opacity = isExiting
    ? getExitOpacity(exitProgress)
    : interpolate(projectProgress, [0, 0.5], [0, 1], CLAMP_BOTH);

  const entryTranslateX = interpolate(
    projectProgress,
    [0, 1],
    [-15, 0],
    CLAMP_BOTH,
  );
  const exitTranslateX = interpolate(exitProgress, [0, 1], [0, 15], CLAMP_BOTH);
  const translateX = isExiting ? exitTranslateX : entryTranslateX;

  return { opacity, translateX };
};

// Alternate layout: avatar-left, avatar-right, ...
export const getLayoutVariant = (index: number): LayoutVariant =>
  index % 2 === 0 ? 'avatar-left' : 'avatar-right';

// Assign start/end frames to feedbacks (sequential, no overlap)
export const calculateFeedbackTimings = (
  feedbacks: FeedbackData[],
): FeedbackTiming[] => {
  const timings: FeedbackTiming[] = [];
  let currentFrame = 0;

  feedbacks.forEach((feedback) => {
    const duration = calculateFeedbackDuration(feedback);
    const startFrame = currentFrame;
    const transitionStartFrame = startFrame + duration;
    const endFrame = transitionStartFrame + TIMING.TRANSITION_DURATION;

    timings.push({
      startFrame,
      transitionStartFrame,
      endFrame,
    });

    currentFrame = endFrame;
  });

  return timings;
};

// Total length of Scene 4 from first feedback start to last feedback end
export const calculateTotalScene4Duration = (
  feedbacks: FeedbackData[] = [],
): number => {
  if (!feedbacks.length) return 0;

  const timings = calculateFeedbackTimings(feedbacks);
  return timings.at(-1)?.endFrame ?? 0;
};

// Visible feedbacks at current frame (with timings and layout)
export const getVisibleFeedbacks = ({
  feedbacks,
  currentFrame,
  animationDelay = 0,
}: {
  feedbacks: FeedbackData[];
  currentFrame: number;
  animationDelay?: number;
}) => {
  // Only process max 3 feedbacks
  const limitedFeedbacks = getRecentFeedbacks(feedbacks);
  const feedbackTimings = calculateFeedbackTimings(limitedFeedbacks);

  return limitedFeedbacks
    .map((feedback, index) => {
      const timing = feedbackTimings[index];
      if (!timing) return null;

      const isLastFeedback = index === limitedFeedbacks.length - 1;
      const isVisible = currentFrame >= timing.startFrame;

      // Last card has no fixed end, others transition out on schedule
      const transitionStartFrame = !isLastFeedback
        ? timing.transitionStartFrame
        : undefined;

      return {
        feedback,
        index,
        isVisible,
        startFrame: timing.startFrame + animationDelay,
        transitionStartFrame: transitionStartFrame
          ? transitionStartFrame + animationDelay
          : undefined,
        variant: getLayoutVariant(index),
      };
    })
    .filter((item) => item !== null);
};
